/**
 * Budget Position Statistics Calculator
 * Calculates totals for enabled positions only across Direct Costs and Pass-Through Costs
 * Following CLAUDE.md standards - direct access, fail-fast, no defensive programming
 */

/**
 * Statistics for enabled positions grouped by phase and unit
 */
export interface PositionStats {
  total: number
  byPhase: Record<string, number>
  byUnit: Record<string, number>
  byPhaseAndUnit: Record<string, Record<string, number>>
}

/**
 * Combined statistics for internal and external positions
 */
export interface BudgetPositionStats {
  internal: PositionStats
  external: PositionStats
  combined: PositionStats
}

/**
 * Calculate statistics for enabled internal positions (Direct Costs)
 * @param budgetPositions - Budget positions array from budget_positions field
 * @returns Statistics for enabled internal positions
 * @throws Error if budgetPositions is not an array
 */
export function calculateInternalPositionStats(budgetPositions: any[]): PositionStats {
  if (!Array.isArray(budgetPositions)) {
    throw new Error('budgetPositions must be an array')
  }

  const stats: PositionStats = {
    total: 0,
    byPhase: {},
    byUnit: {},
    byPhaseAndUnit: {}
  }

  budgetPositions.forEach(category => {
    if (!category.department_roles || !Array.isArray(category.department_roles)) {
      return
    }

    category.department_roles.forEach((role: any) => {
      // ONLY count enabled positions
      if (role.enabled !== true) {
        return
      }

      stats.total++

      // Count by phase (always use phase_id for consistency)
      if (role.phase_id) {
        const phaseKey = role.phase_id
        stats.byPhase[phaseKey] = (stats.byPhase[phaseKey] || 0) + 1

        // Count by phase and unit
        if (role.unit_name) {
          if (!stats.byPhaseAndUnit[phaseKey]) {
            stats.byPhaseAndUnit[phaseKey] = {}
          }
          stats.byPhaseAndUnit[phaseKey][role.unit_name] =
            (stats.byPhaseAndUnit[phaseKey][role.unit_name] || 0) + 1
        }
      }

      // Count by unit
      if (role.unit_name) {
        stats.byUnit[role.unit_name] = (stats.byUnit[role.unit_name] || 0) + 1
      }
    })
  })

  return stats
}

/**
 * Calculate statistics for enabled external positions (Pass-Through Costs)
 * @param externalPositions - External positions array from external_positions field
 * @returns Statistics for enabled external positions
 * @throws Error if externalPositions is not an array
 */
export function calculateExternalPositionStats(externalPositions: any[]): PositionStats {
  if (!Array.isArray(externalPositions)) {
    throw new Error('externalPositions must be an array')
  }

  const stats: PositionStats = {
    total: 0,
    byPhase: {},
    byUnit: {},
    byPhaseAndUnit: {}
  }

  externalPositions.forEach((position: any) => {
    // ONLY count enabled positions
    if (position.enabled !== true) {
      return
    }

    stats.total++

    // Count by phase (always use phase_id for consistency)
    if (position.phase_id) {
      const phaseKey = position.phase_id
      stats.byPhase[phaseKey] = (stats.byPhase[phaseKey] || 0) + 1

      // Count by phase and unit
      if (position.unit_name) {
        if (!stats.byPhaseAndUnit[phaseKey]) {
          stats.byPhaseAndUnit[phaseKey] = {}
        }
        stats.byPhaseAndUnit[phaseKey][position.unit_name] =
          (stats.byPhaseAndUnit[phaseKey][position.unit_name] || 0) + 1
      }
    }

    // Count by unit
    if (position.unit_name) {
      stats.byUnit[position.unit_name] = (stats.byUnit[position.unit_name] || 0) + 1
    }
  })

  return stats
}

/**
 * Merge two PositionStats objects
 * @param stats1 - First stats object
 * @param stats2 - Second stats object
 * @returns Merged stats
 */
function mergeStats(stats1: PositionStats, stats2: PositionStats): PositionStats {
  const merged: PositionStats = {
    total: stats1.total + stats2.total,
    byPhase: { ...stats1.byPhase },
    byUnit: { ...stats1.byUnit },
    byPhaseAndUnit: { ...stats1.byPhaseAndUnit }
  }

  // Merge byPhase
  Object.keys(stats2.byPhase).forEach(phase => {
    merged.byPhase[phase] = (merged.byPhase[phase] || 0) + stats2.byPhase[phase]
  })

  // Merge byUnit
  Object.keys(stats2.byUnit).forEach(unit => {
    merged.byUnit[unit] = (merged.byUnit[unit] || 0) + stats2.byUnit[unit]
  })

  // Merge byPhaseAndUnit
  Object.keys(stats2.byPhaseAndUnit).forEach(phase => {
    if (!merged.byPhaseAndUnit[phase]) {
      merged.byPhaseAndUnit[phase] = {}
    }
    Object.keys(stats2.byPhaseAndUnit[phase]).forEach(unit => {
      merged.byPhaseAndUnit[phase][unit] =
        (merged.byPhaseAndUnit[phase][unit] || 0) + stats2.byPhaseAndUnit[phase][unit]
    })
  })

  return merged
}

/**
 * Calculate comprehensive statistics for all enabled positions in a budget
 * ONLY counts positions with enabled === true
 * @param budgetPositions - Internal positions (Direct Costs)
 * @param externalPositions - External positions (Pass-Through Costs)
 * @returns Complete statistics for internal, external, and combined positions
 * @throws Error if inputs are not arrays
 * @example
 * const stats = calculateBudgetPositionStats(
 *   budget.budget_positions,
 *   budget.external_positions
 * );
 * console.log(`Total enabled positions: ${stats.combined.total}`);
 * console.log(`Enabled positions by phase:`, stats.combined.byPhase);
 * console.log(`Enabled positions by unit:`, stats.combined.byUnit);
 */
export function calculateBudgetPositionStats(
  budgetPositions: any[],
  externalPositions: any[]
): BudgetPositionStats {
  const internal = calculateInternalPositionStats(budgetPositions)
  const external = calculateExternalPositionStats(externalPositions)
  const combined = mergeStats(internal, external)

  return {
    internal,
    external,
    combined
  }
}

/**
 * Format position stats as a human-readable summary
 * @param stats - Position statistics
 * @param label - Label for the stats (e.g., "Internal", "External", "All")
 * @returns Formatted string summary
 * @example
 * const summary = formatPositionStatsSummary(stats.internal, "Direct Costs");
 * console.log(summary);
 * // Output:
 * // Direct Costs: 25 enabled positions
 * //   By Phase:
 * //     Setup & Initiation: 10
 * //     Recruitment: 15
 * //   By Unit:
 * //     Month: 18
 * //     Sites: 7
 */
export function formatPositionStatsSummary(stats: PositionStats, label: string): string {
  const lines: string[] = []
  lines.push(`${label}: ${stats.total} enabled positions`)

  if (Object.keys(stats.byPhase).length > 0) {
    lines.push('  By Phase:')
    Object.entries(stats.byPhase)
      .sort(([, a], [, b]) => b - a)
      .forEach(([phase, count]) => {
        lines.push(`    ${phase}: ${count}`)
      })
  }

  if (Object.keys(stats.byUnit).length > 0) {
    lines.push('  By Unit:')
    Object.entries(stats.byUnit)
      .sort(([, a], [, b]) => b - a)
      .forEach(([unit, count]) => {
        lines.push(`    ${unit}: ${count}`)
      })
  }

  return lines.join('\n')
}

/**
 * Position with invalid quantity
 */
export interface PositionWithInvalidQuantity {
  id: string
  key?: string
  position_name: string
  phase_name?: string
  unit_name?: string
  quantity: any
  type: 'internal' | 'external'
  category_name?: string
}

/**
 * Find phases that have no enabled positions assigned
 * @param phases - Array of phase objects from budget
 * @param stats - Position statistics from calculateBudgetPositionStats
 * @returns Array of phase objects that have zero enabled positions
 * @throws Error if phases is not an array
 * @example
 * const emptyPhases = getEmptyPhases(budget.phases, stats.combined);
 * console.log(emptyPhases);
 * // [{ id: "phase-123", name: "Unused Phase", start_date: "2025-01-01", ... }]
 */
export function getEmptyPhases(phases: any[], stats: PositionStats): any[] {
  if (!Array.isArray(phases)) {
    throw new Error('phases must be an array')
  }

  return phases.filter(phase => {
    // Use phase.id (UUID) to match against stats which are keyed by phase_id
    const count = stats.byPhase[phase.id] || 0
    return count === 0
  })
}

/**
 * Find enabled positions with invalid quantity (null/NaN/undefined/0)
 * ONLY checks enabled positions
 * @param budgetPositions - Internal positions (Direct Costs)
 * @param externalPositions - External positions (Pass-Through Costs)
 * @returns Array of enabled positions with invalid quantities
 * @throws Error if inputs are not arrays
 * @example
 * const invalidPositions = getPositionsWithInvalidQuantity(
 *   budget.budget_positions,
 *   budget.external_positions
 * );
 * console.log(invalidPositions);
 * // [
 * //   { id: "pos-1", position_name: "Manager", quantity: 0, type: "internal" },
 * //   { id: "pos-2", position_name: "Consultant", quantity: null, type: "external" }
 * // ]
 */
export function getPositionsWithInvalidQuantity(
  budgetPositions: any[],
  externalPositions: any[]
): PositionWithInvalidQuantity[] {
  if (!Array.isArray(budgetPositions)) {
    throw new Error('budgetPositions must be an array')
  }
  if (!Array.isArray(externalPositions)) {
    throw new Error('externalPositions must be an array')
  }

  const invalid: PositionWithInvalidQuantity[] = []

  // Check internal positions (ONLY enabled)
  budgetPositions.forEach(category => {
    if (!category.department_roles || !Array.isArray(category.department_roles)) {
      return
    }

    category.department_roles.forEach((role: any) => {
      // ONLY check enabled positions
      if (role.enabled !== true) {
        return
      }

      const qty = role.quantity

      // Check if quantity is null/undefined/NaN/0
      if (qty === null || qty === undefined || isNaN(qty) || qty === 0) {
        invalid.push({
          id: role.id,
          key: role.key,
          position_name: role.position_name,
          phase_name: role.phase_name,
          unit_name: role.unit_name,
          quantity: qty,
          type: 'internal',
          category_name: category.name
        })
      }
    })
  })

  // Check external positions (ONLY enabled)
  externalPositions.forEach((position: any) => {
    // ONLY check enabled positions
    if (position.enabled !== true) {
      return
    }

    const qty = position.quantity

    // Check if quantity is null/undefined/NaN/0
    if (qty === null || qty === undefined || isNaN(qty) || qty === 0) {
      invalid.push({
        id: position.id,
        position_name: position.position_name,
        phase_name: position.phase_name,
        unit_name: position.unit_name,
        quantity: qty,
        type: 'external'
      })
    }
  })

  return invalid
}

/**
 * Position without timeline dates
 */
export interface PositionWithoutTimeline {
  id: string
  key?: string
  position_name: string
  phase_name?: string
  phase_id?: string
  unit_name?: string
  start_date?: string
  end_date?: string
  type: 'internal' | 'external'
  category_name?: string
}

/**
 * Find enabled positions without timeline dates (missing start_date or end_date)
 * ONLY checks enabled positions
 * @param budgetPositions - Internal positions (Direct Costs)
 * @param externalPositions - External positions (Pass-Through Costs)
 * @returns Array of enabled positions without timelines
 * @throws Error if inputs are not arrays
 * @example
 * const noTimelines = getPositionsWithoutTimelines(
 *   budget.budget_positions,
 *   budget.external_positions
 * );
 * console.log(noTimelines);
 * // [
 * //   { id: "pos-1", position_name: "Manager", start_date: "", end_date: "", type: "internal" }
 * // ]
 */
export function getPositionsWithoutTimelines(
  budgetPositions: any[],
  externalPositions: any[]
): PositionWithoutTimeline[] {
  if (!Array.isArray(budgetPositions)) {
    throw new Error('budgetPositions must be an array')
  }
  if (!Array.isArray(externalPositions)) {
    throw new Error('externalPositions must be an array')
  }

  const withoutTimelines: PositionWithoutTimeline[] = []

  // Check internal positions (ONLY enabled)
  budgetPositions.forEach(category => {
    if (!category.department_roles || !Array.isArray(category.department_roles)) {
      return
    }

    category.department_roles.forEach((role: any) => {
      // ONLY check enabled positions
      if (role.enabled !== true) {
        return
      }

      const startDate = role.start_date
      const endDate = role.end_date

      // Check if start_date or end_date is missing/empty
      if (!startDate || !endDate || startDate === '' || endDate === '') {
        const positionData = {
          id: role.id,
          key: role.key,
          position_name: role.position_name,
          phase_name: role.phase_name,
          phase_id: role.phase_id,
          unit_name: role.unit_name,
          start_date: startDate,
          end_date: endDate,
          type: 'internal' as const,
          category_name: category.name
        }

        withoutTimelines.push(positionData)
      }
    })
  })

  // Check external positions (ONLY enabled)
  externalPositions.forEach((position: any) => {
    // ONLY check enabled positions
    if (position.enabled !== true) {
      return
    }

    const startDate = position.start_date
    const endDate = position.end_date

    // Check if start_date or end_date is missing/empty
    if (!startDate || !endDate || startDate === '' || endDate === '') {
      const positionData = {
        id: position.id,
        position_name: position.position_name,
        phase_name: position.phase_name,
        phase_id: position.phase_id,
        unit_name: position.unit_name,
        start_date: startDate,
        end_date: endDate,
        type: 'external' as const
      }

      withoutTimelines.push(positionData)
    }
  })

  return withoutTimelines
}

/**
 * Position missing phase assignment
 */
export interface PositionWithoutPhase {
  id: string
  key?: string
  position_name: string
  unit_name?: string
  type: 'internal' | 'external'
  category_name?: string
}

/**
 * Find enabled positions without phase assignment
 * ONLY checks enabled positions
 * @param budgetPositions - Internal positions (Direct Costs)
 * @param externalPositions - External positions (Pass-Through Costs)
 * @returns Array of enabled positions without phases
 * @throws Error if inputs are not arrays
 * @example
 * const noPhases = getPositionsWithoutPhases(
 *   budget.budget_positions,
 *   budget.external_positions
 * );
 */
export function getPositionsWithoutPhases(
  budgetPositions: any[],
  externalPositions: any[]
): PositionWithoutPhase[] {
  if (!Array.isArray(budgetPositions)) {
    throw new Error('budgetPositions must be an array')
  }
  if (!Array.isArray(externalPositions)) {
    throw new Error('externalPositions must be an array')
  }

  const withoutPhases: PositionWithoutPhase[] = []

  // Check internal positions (ONLY enabled)
  budgetPositions.forEach(category => {
    if (!category.department_roles || !Array.isArray(category.department_roles)) {
      return
    }

    category.department_roles.forEach((role: any) => {
      // ONLY check enabled positions
      if (role.enabled !== true) {
        return
      }

      // Check if phase_id is missing/empty/null
      if (!role.phase_id || role.phase_id === '' || role.phase_id === null) {
        withoutPhases.push({
          id: role.id,
          key: role.key,
          position_name: role.position_name,
          unit_name: role.unit_name,
          type: 'internal',
          category_name: category.name
        })
      }
    })
  })

  // Check external positions (ONLY enabled)
  externalPositions.forEach((position: any) => {
    // ONLY check enabled positions
    if (position.enabled !== true) {
      return
    }

    // Check if phase_id is missing/empty/null
    if (!position.phase_id || position.phase_id === '' || position.phase_id === null) {
      withoutPhases.push({
        id: position.id,
        position_name: position.position_name,
        unit_name: position.unit_name,
        type: 'external'
      })
    }
  })

  return withoutPhases
}

/**
 * Position-based completion metrics
 */
export interface PositionCompletionMetrics {
  totalEnabledPositions: number
  positionsWithPhase: number
  positionsWithTimelines: number
  positionsWithValidQuantity: number
  completionPercentage: number
  positionsWithoutPhases: PositionWithoutPhase[]
  positionsWithoutTimelines: PositionWithoutTimeline[]
  invalidQuantityPositions: PositionWithInvalidQuantity[]
}

/**
 * Calculate position-based completion percentage
 * For each enabled position, checks 3 criteria:
 * 1. Has phase_id
 * 2. Has start_date and end_date
 * 3. Has quantity > 0
 *
 * Completion % = (total criteria met) / (total positions * 3) * 100
 *
 * @param budgetPositions - Internal positions (Direct Costs)
 * @param externalPositions - External positions (Pass-Through Costs)
 * @returns Completion metrics
 * @throws Error if inputs are not arrays
 * @example
 * const metrics = calculatePositionCompletionMetrics(
 *   budget.budget_positions,
 *   budget.external_positions
 * );
 * console.log(`Completion: ${metrics.completionPercentage}%`);
 * console.log(`${metrics.positionsWithPhase} / ${metrics.totalEnabledPositions} have phases`);
 */
export function calculatePositionCompletionMetrics(
  budgetPositions: any[],
  externalPositions: any[]
): PositionCompletionMetrics {
  if (!Array.isArray(budgetPositions)) {
    throw new Error('budgetPositions must be an array')
  }
  if (!Array.isArray(externalPositions)) {
    throw new Error('externalPositions must be an array')
  }

  // Count total enabled positions
  const stats = calculateBudgetPositionStats(budgetPositions, externalPositions)
  const totalEnabledPositions = stats.combined.total

  // If no enabled positions, return 0%
  if (totalEnabledPositions === 0) {
    return {
      totalEnabledPositions: 0,
      positionsWithPhase: 0,
      positionsWithTimelines: 0,
      positionsWithValidQuantity: 0,
      completionPercentage: 0,
      positionsWithoutPhases: [],
      positionsWithoutTimelines: [],
      invalidQuantityPositions: []
    }
  }

  // Get positions missing data
  const positionsWithoutPhases = getPositionsWithoutPhases(budgetPositions, externalPositions)
  const positionsWithoutTimelines = getPositionsWithoutTimelines(budgetPositions, externalPositions)
  const invalidQuantityPositions = getPositionsWithInvalidQuantity(budgetPositions, externalPositions)

  // Calculate positions WITH valid data
  const positionsWithPhase = totalEnabledPositions - positionsWithoutPhases.length
  const positionsWithTimelines = totalEnabledPositions - positionsWithoutTimelines.length
  const positionsWithValidQuantity = totalEnabledPositions - invalidQuantityPositions.length

  // Calculate completion percentage
  // Each position has 3 criteria: phase, timeline, quantity
  const totalCriteria = totalEnabledPositions * 3
  const metCriteria = positionsWithPhase + positionsWithTimelines + positionsWithValidQuantity
  const completionPercentage = Math.round((metCriteria / totalCriteria) * 100)

  return {
    totalEnabledPositions,
    positionsWithPhase,
    positionsWithTimelines,
    positionsWithValidQuantity,
    completionPercentage,
    positionsWithoutPhases,
    positionsWithoutTimelines,
    invalidQuantityPositions
  }
}
